import Vue from 'vue';
import axios from 'axios';
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import {IErrors} from '@/model/IErrors';
import {settings} from '@/plugins/settings';
import responseHandler from '@/plugins/errorHandling';
import {ICommunity, IShortCommunity, ICommunityLocker, ISecurityCode, IModule, ICommunityContact, ICommunityIntegrationData} from '@/model/Community/communityInterfaces';
import {securityCodeService} from '@/services/securityCodeService';
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from '@/constants/pagination';

interface CommunityState {
  community: ICommunity | null;
  communityList: IShortCommunity[];
  communityLockers: ICommunityLocker[];
  communityContacts: ICommunityContact[];
  countries: { id: number; name: string }[];
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  lockersCurrentPage: number;
  lockersLastPage: number;
  lockersTotal: number;
  errors: IErrors;
  securityCodeList: ISecurityCode[];
  scCurrentPage: number;
  scLastPage: number;
  scTotal: number;
  scPerPage: number;
  pinSectionVisible: boolean;
  availableModules: IModule[];
}

const state: CommunityState = {
  community: null,
  communityList: [],
  communityLockers: [],
  communityContacts: [],
  countries: [],
  currentPage: PAGINATION_FIRST_PAGE,
  lastPage: PAGINATION_DEFAULT_LAST_PAGE,
  total: PAGINATION_DEFAULT_TOTAL,
  perPage: PAGINATION_DEFAULT_PER_PAGE,
  lockersCurrentPage: PAGINATION_FIRST_PAGE,
  lockersLastPage: PAGINATION_DEFAULT_LAST_PAGE,
  lockersTotal: PAGINATION_DEFAULT_TOTAL,
  errors: {},
  securityCodeList: [],
  scCurrentPage: PAGINATION_FIRST_PAGE,
  scLastPage: PAGINATION_DEFAULT_LAST_PAGE,
  scTotal: PAGINATION_DEFAULT_TOTAL,
  scPerPage: PAGINATION_DEFAULT_PER_PAGE,
  pinSectionVisible: false,
  availableModules: [],
};

const getters: GetterTree<CommunityState, RootState> = {
  community: (state) => state.community,
  communityList: (state) => state.communityList,
  communityLockers: (state) => state.communityLockers,
  communityContacts: (state) => state.communityContacts,
  countries: (state) => state.countries,
  currentPage: (state) => state.currentPage,
  lastPage: (state) => state.lastPage,
  total: (state) => state.total,
  perPage: (state) => state.perPage,
  lockersCurrentPage: (state) => state.lockersCurrentPage,
  lockersLastPage: (state) => state.lockersLastPage,
  lockersTotal: (state) => state.lockersTotal,
  errors: (state) => state.errors,
  securityCodeList: (state) => state.securityCodeList,
  scCurrentPage: (state) => state.scCurrentPage,
  scLastPage: (state) => state.scLastPage,
  scTotal: (state) => state.scTotal,
  scPerPage: (state) => state.scPerPage,
  pinSectionVisible: (state) => state.pinSectionVisible,
  availableModules: (state) => state.availableModules,
};

const actions: ActionTree<CommunityState, RootState> = {

  fetchCommunities: ({commit}, filters): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities',
      method: 'GET',
      params: filters,
    }),
    commit, 'communitiesLoaded'
  ),

  fetchAllCommunities: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/all',
      method: 'GET',
    }),
    commit, 'communitiesLoaded'
  ),

  clearCommunities: ({commit}) => commit('clearCommunities'),

  fetchCommunity: ({commit}, communityId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/' + communityId,
    }),
    commit, 'communityLoaded'
  ),

  resetCommunity: ({commit}) => commit('communityLoaded', null),

  createCommunity: ({commit}, data): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities',
      method: 'POST',
      data,
    }),
    commit, 'communityCreated'
  ),

  updateCommunity: ({commit}, {communityId, data}: {communityId: number; data: any}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/' + communityId,
      method: 'PATCH',
      data,
    }),
    commit, 'communityUpdated'
  ),

  fetchCommunityContacts: ({commit}, communityId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/' + communityId + '/contacts',
      method: 'GET',
    }),
    commit, 'communityContactsLoaded'
  ),

  addCommunityContact: ({commit}, {communityId, data}: {communityId: number; data: {user_email: string; role: string}}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/' + communityId + '/contacts',
      method: 'POST',
      data,
    }),
    commit, 'communityContactAdded'
  ),

  fetchCommunityLockers: ({commit}, {communityId, filters}: {communityId: number; filters?: any}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/' + communityId + '/lockers',
      method: 'GET',
      params: filters,
    }),
    commit, 'communityLockersLoaded'
  ),

  fetchModules: ({commit, state}): any => responseHandler.debounce<IModule[]>(
    state.availableModules, 300,
    () => axios({
      url: settings.endpoint + 'communities/modules',
    }),
    commit, 'modulesLoaded'
  ),

  reloadModules: ({commit}): any => {
    return axios({
      url: settings.endpoint + 'communities/modules',
    }).then((response: any) => {
      commit('modulesLoaded', response.data.data);
    });
  },

  createModule: ({commit, dispatch}, data: {name: string; icon: string | null; description: string | null}): any => {
    return axios({
      url: settings.endpoint + 'communities/modules',
      method: 'POST',
      data,
    }).then((response: any) => {
      dispatch('reloadModules');
      return response.data;
    });
  },

  updateModule: ({commit, dispatch}, {id, ...data}: {id: number; name: string; icon: string | null; description: string | null}): any => {
    return axios({
      url: settings.endpoint + 'communities/modules/' + id,
      method: 'PATCH',
      data,
    }).then((response: any) => {
      dispatch('reloadModules');
      return response.data;
    });
  },

  createSubmodule: ({commit}, {moduleId, ...data}: {moduleId: number; name: string; description: string | null}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/modules/' + moduleId + '/submodules',
      method: 'POST',
      data,
    }),
    commit, 'submoduleSaved', null, false
  ),

  updateSubmodule: ({commit}, {id, ...data}: {id: number; name: string; description: string | null}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/submodules/' + id,
      method: 'PATCH',
      data,
    }),
    commit, 'submoduleSaved', null, false
  ),

  fetchCommunityIntegrationsData: (_, communityId: number): Promise<ICommunityIntegrationData[]> => axios({
    url: settings.endpoint + 'communities/' + communityId + '/integrations-data',
  }).then((response: any) => response.data.data),

  fetchCountries: ({commit, state}): any => responseHandler.debounce<{ id: number; name: string }[]>(
    state.countries, 300,
    () => axios({
      url: settings.endpoint + 'communities/countries',
    }),
    commit, 'countriesLoaded'
  ),

  toggleCommunityPinMode: ({commit}, {communityId, enabled}: {communityId: number; enabled: boolean}): any => {
    return axios({
      url: settings.endpoint + 'communities/' + communityId + '/pin-mode',
      method: 'PATCH',
      data: { enabled },
    }).then(() => commit('communityPinModeSet', {communityId, enabled}));
  },

  deleteCommunity: (_, communityId: number): any => axios({
    url: settings.endpoint + 'communities/' + communityId,
    method: 'DELETE',
  }),

  restoreCommunity: (_, communityId: number): any => axios({
    url: settings.endpoint + 'communities/' + communityId + '/restore',
    method: 'PATCH',
  }),

  patchCommunity: ({commit}, {communityId, data}: {communityId: number; data: any}): any => {
    commit('errors', {});
    return axios({
      url: settings.endpoint + 'communities/' + communityId,
      method: 'PATCH',
      data,
    }).then((response: any) => {
      const community = response.data && response.data.data ? response.data.data : response.data;
      commit('communityUpdated', community);
      return community;
    });
  },

  generateSecurityCode: (_: any, communityId?: number): any => {
    return axios({
      url: settings.endpoint + 'communities/security-codes',
      method: 'POST',
      data: communityId ? { communityId } : {},
    }).then((response: any) => {
      const d = response.data;
      return d && d.code ? d.code : (d && d.data ? d.data.code : null);
    });
  },

  fetchSecurityCodeList: ({commit}, filters: any): any => responseHandler.handle(
    () => axios({ url: settings.endpoint + 'security-codes', method: 'GET', params: filters }),
    commit, 'securityCodeListLoaded'
  ),

  clearSecurityCodeList: ({commit}) => commit('securityCodeListLoaded', null),

  checkPinSectionVisibility: ({commit}): any => {
    return securityCodeService.myPin().then((result: any) => {
      commit('pinSectionVisibleSet', !!(result && result.community_id));
    }).catch(() => {
      commit('pinSectionVisibleSet', false);
    });
  },

  errors: ({commit}, errors: IErrors): any => commit('errors', errors),
  cleanError: ({commit}, index: any): any => commit('cleanError', index),

};

const mutations: MutationTree<CommunityState> = {

  communitiesLoaded(state, payload: any) {
    if (!payload) {
      state.communityList = [];
      return;
    }
    state.communityList = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.currentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.lastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.total = payload.total || PAGINATION_DEFAULT_TOTAL;
    state.perPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
  },

  clearCommunities(state) {
    state.communityList = [];
    state.currentPage = PAGINATION_FIRST_PAGE;
    state.lastPage = PAGINATION_DEFAULT_LAST_PAGE;
    state.total = PAGINATION_DEFAULT_TOTAL;
  },

  communityLoaded(state, payload: ICommunity | null) {
    state.community = payload;
  },

  communityCreated(state, payload: ICommunity) {
    state.community = payload;
  },

  communityUpdated(state, payload: ICommunity) {
    state.community = payload;
    const idx = state.communityList.findIndex(c => c.id === payload.id);
    if (idx !== -1) {
      Vue.set(state.communityList, idx, payload);
    }
  },

  communityPinModeSet(state, payload: {communityId: number; enabled: boolean}) {
    if (state.community && state.community.id === payload.communityId) {
      Vue.set(state.community, 'use_security_code', payload.enabled);
    }
  },

  communityContactsLoaded(state, payload: any) {
    state.communityContacts = Array.isArray(payload) ? payload : (payload && Array.isArray(payload.data) ? payload.data : []);
  },


  communityContactAdded(state, payload: ICommunityContact) {
    if (payload) {
      state.communityContacts.push(payload);
    }
  },

  communityLockersLoaded(state, payload: any) {
    state.communityLockers = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.lockersCurrentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.lockersLastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.lockersTotal = payload.total || PAGINATION_DEFAULT_TOTAL;
  },

  countriesLoaded(state, payload: any) {
    state.countries = Array.isArray(payload) ? payload : [];
  },

  modulesLoaded(state, payload: any) {
    state.availableModules = Array.isArray(payload) ? payload : [];
  },

  moduleCreated(state, module: IModule) {
    state.availableModules = [...state.availableModules, module];
  },

  moduleUpdated(state, updated: IModule) {
    state.availableModules = state.availableModules.map((m) => m.id === updated.id ? updated : m);
  },

  securityCodeListLoaded(state, payload: any) {
    if (!payload) {
      state.securityCodeList = [];
      state.scCurrentPage = PAGINATION_FIRST_PAGE;
      state.scLastPage = PAGINATION_DEFAULT_LAST_PAGE;
      state.scTotal = PAGINATION_DEFAULT_TOTAL;
      state.scPerPage = PAGINATION_DEFAULT_PER_PAGE;
      return;
    }
    state.securityCodeList = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.scCurrentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.scLastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.scTotal = payload.total || PAGINATION_DEFAULT_TOTAL;
    state.scPerPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
  },

  pinSectionVisibleSet(state, visible: boolean) {
    state.pinSectionVisible = visible;
  },

  errors(state, payload: IErrors) {
    state.errors = payload;
  },

  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },

};

const namespaced: boolean = true;

export const communityModule: Module<CommunityState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
